import React, { useEffect, useMemo, useState } from 'react';
import { MultipleChoice } from './MultipleChoice';
import * as styles from './SuggestibleMultipleChoice.module.scss';
import { VisuallyHidden } from 'components/core';
import { MultipleChoiceBlock } from 'concepts/Extraction/Blocks/MultipleChoice/MultipleChoiceBlock';
import { HydratedBlockProps } from 'concepts/Extraction/Blocks/BaseBlock';
import { useReviewFeatures } from 'hooks/reviews/useReviewFeatures';
import { useAutoDocExtSuggestion } from 'concepts/AutoDocExtraction/useAutoDocExtSuggestion';
import {
  useAutoDocExtSuggestionContext,
  getSingleExtraction,
} from 'concepts/AutoDocExtraction/AutoDocExtSuggestionProvider';
import { deriveExtractionSuggestion } from 'concepts/Extraction/Blocks/TextField/deriveExtractionSuggestion';
import { isFeatureEnabledForField } from 'concepts/AutoDocExtraction/fieldFlags';
import { matchSuggestionToOption } from 'concepts/AutoDocExtraction/optionMatching/matchSuggestionToOption';
import { QuotesSection } from 'concepts/AutoDocExtraction/QuotesSection/QuotesSection';
import { SuggestionActionButtons } from 'concepts/AutoDocExtraction/SuggestionActionButtons/SuggestionActionButtons';
import {
  trackSuggestionViewed,
  trackSuggestionDecision,
  trackSuggestionNotDisplayable,
} from 'concepts/Extraction/Extract/tracking';
import { SuggestionTrigger } from 'concepts/AutoDocExtraction/tracking';
import { usePrevious } from 'hooks/usePrevious';
import { getDE2SuggestionStatusStorageKey } from 'concepts/AutoDocExtraction/suggestionStatusStorage';

export type SuggestibleMultipleChoiceProps = {
  block: MultipleChoiceBlock & HydratedBlockProps;
  value: string | null;
  comments: string | null;
  onChange: (newValue: string | null) => void;
  onChangeComments: (newComments: string) => void;
};

const PlainMultipleChoice = (props: SuggestibleMultipleChoiceProps) => (
  <MultipleChoice
    editable
    block={props.block}
    value={props.value}
    comments={props.comments}
    onChange={props.onChange}
    onChangeComments={props.onChangeComments}
  />
);

const SuggestibleMultipleChoiceEnabled = ({
  block,
  value,
  comments,
  onChange,
  onChangeComments,
  autoDocExtField,
}: SuggestibleMultipleChoiceProps & { autoDocExtField: string }) => {
  const {
    reviewId,
    studyId,
    documentId,
    useSuggestions,
    allSuggestions,
  } = useAutoDocExtSuggestionContext();
  const autoDocumentExtraction = getSingleExtraction(
    allSuggestions,
    autoDocExtField
  );
  const { suggestion } = deriveExtractionSuggestion(autoDocumentExtraction);

  const match = useMemo(
    () =>
      suggestion
        ? matchSuggestionToOption(suggestion, block, autoDocExtField)
        : null,
    [suggestion, block, autoDocExtField]
  );

  const { suggestedValue, suggestionLabel } = useMemo(() => {
    if (!match) return { suggestedValue: null, suggestionLabel: null };
    if (match.kind === 'option') {
      return {
        suggestedValue: match.optionId,
        suggestionLabel:
          block.options.find((o) => o.id === match.optionId)?.label ?? null,
      };
    }
    return { suggestedValue: match.text, suggestionLabel: match.text };
  }, [match, block.options]);

  const hasStorageAnchor = documentId !== undefined;
  const storageKey = getDE2SuggestionStatusStorageKey({ reviewId, documentId });

  const {
    showSuggestion,
    accept,
    reject,
    suggestionStatus,
  } = useAutoDocExtSuggestion({
    fieldName: block.id,
    suggestion: suggestionLabel,
    currentValue: value,
    enabled: hasStorageAnchor && suggestedValue !== null,
    storageKey,
  });

  const trackingData = useMemo(
    () => ({
      field: autoDocExtField,
      field_alias: block.label,
      field_type: block.type,
      review_id: reviewId,
      study_id: studyId,
      document_id: documentId ?? null,
      suggestion,
      suggestion_label: suggestionLabel,
    }),
    [
      autoDocExtField,
      block.label,
      block.type,
      reviewId,
      studyId,
      documentId,
      suggestion,
      suggestionLabel,
    ]
  );

  // Fire `metadata_suggestion_viewed` exactly once each time the suggestion
  // UI transitions into being shown.
  const wasPreviouslyShowingSuggestion = usePrevious(showSuggestion);
  useEffect(() => {
    if (!wasPreviouslyShowingSuggestion && showSuggestion && suggestion) {
      trackSuggestionViewed(trackingData);
    }
  }, [wasPreviouslyShowingSuggestion, showSuggestion, suggestion]);

  // Fire `metadata_suggestion_not_displayable` once when a suggestion loads
  // but cannot be shown — no option match and no "other" field on the block.
  const previousSuggestion = usePrevious(suggestion);
  useEffect(() => {
    if (!previousSuggestion && suggestion && match === null && useSuggestions) {
      trackSuggestionNotDisplayable(trackingData);
    }
  }, [previousSuggestion, suggestion, match, useSuggestions]);

  // Set when the user accepts or rejects so the radio group can take focus
  // once the suggestion UI goes away — keyboard users would otherwise lose
  // their place when the button they pressed disappears.
  const [shouldFocusField, setShouldFocusField] = useState(false);

  const handleAccept = (trigger: SuggestionTrigger) => {
    if (!showSuggestion) return;
    setShouldFocusField(true);
    accept();
    if (suggestedValue !== null) onChange(suggestedValue);
    trackSuggestionDecision('accepted', { ...trackingData, trigger });
  };

  const handleReject = (trigger: SuggestionTrigger) => {
    if (!showSuggestion) return;
    setShouldFocusField(true);
    reject();
    trackSuggestionDecision('rejected', { ...trackingData, trigger });
  };

  useEffect(() => {
    if (showSuggestion || !shouldFocusField) return;
    const radios = Array.from(
      document.getElementsByName(block.id)
    ) as HTMLInputElement[];
    const target = radios.find((radio) => radio.checked) ?? radios[0];
    target?.focus();
    setShouldFocusField(false);
  }, [showSuggestion, shouldFocusField, block.id]);

  const visibleValue = showSuggestion ? suggestedValue : value;

  const wrappedOnChange = (newValue: string | null) => {
    if (showSuggestion) {
      handleReject('input');
    }
    onChange(newValue);
  };

  const handleClickOption = (optionId: string) => {
    if (showSuggestion && optionId === suggestedValue) {
      handleAccept('input');
    }
  };

  const suggestionPending = showSuggestion && suggestionLabel !== null;
  const suggestionIsOther = match?.kind === 'other';

  const suggestionButtons = suggestionPending ? (
    <SuggestionActionButtons
      suggestionLabel={suggestionLabel}
      size="md"
      onAccept={() => handleAccept('button')}
      onReject={() => handleReject('button')}
    />
  ) : null;

  const helperId = `${block.id}-suggestion-helper`;
  const otherHelperId = `${block.id}-other-suggestion-helper`;

  return (
    <>
      {suggestionPending && !suggestionIsOther && (
        <VisuallyHidden id={helperId}>
          Suggested value, not yet saved. Tab to the Accept and Reject buttons.
        </VisuallyHidden>
      )}
      {suggestionPending && suggestionIsOther && (
        <VisuallyHidden id={otherHelperId}>
          Suggested value, not yet saved. Focus this field to accept.
        </VisuallyHidden>
      )}
      <MultipleChoice
        editable
        block={block}
        value={visibleValue}
        comments={comments}
        onChange={wrappedOnChange}
        onClickOption={handleClickOption}
        onOtherFieldFocus={() => handleAccept('input')}
        onOtherFieldClick={() => handleAccept('input')}
        onChangeComments={onChangeComments}
        radioGroupClassName={
          showSuggestion ? styles.suggestionPending : undefined
        }
        radioGroupDescribedBy={
          suggestionPending && !suggestionIsOther ? helperId : undefined
        }
        otherTextFieldDescribedBy={
          suggestionPending && suggestionIsOther ? otherHelperId : undefined
        }
        trailingContent={suggestionButtons}
        hideClearSelection={!!suggestionButtons}
      />
      <QuotesSection
        autoDocExtField={autoDocExtField}
        trackedField={autoDocExtField}
        suggestionStatus={suggestionStatus}
        studyId={studyId}
        extractionTool="extraction2"
      />
    </>
  );
};

const SuggestibleMultipleChoice = (props: SuggestibleMultipleChoiceProps) => {
  const { autoDocExtField } = props.block;
  const { hasFeature } = useReviewFeatures();

  if (
    autoDocExtField !== undefined &&
    isFeatureEnabledForField(autoDocExtField, hasFeature)
  ) {
    return (
      <SuggestibleMultipleChoiceEnabled
        {...props}
        autoDocExtField={autoDocExtField}
      />
    );
  }

  return <PlainMultipleChoice {...props} />;
};

export default SuggestibleMultipleChoice;
